import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };

  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

export default function JS_Const() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🔒 JavaScript const Keyword</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        The <b>const</b> keyword was introduced in <b>ES6 (2015)</b>. It’s used
        to declare variables whose values <b>cannot be reassigned</b>. Use{" "}
        <code>const</code> when you know the variable’s value will stay the same
        throughout the program.
      </p>

      {/* Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0fdfa, #ccfbf1)",
        }}
      >
        <h2 style={{ color: "#0f766e" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code>const</code> variables are <b>block scoped</b>.
          </li>
          <li>
            They <b>cannot be reassigned</b> after declaration.
          </li>
          <li>
            They must be <b>initialized</b> at the time of declaration.
          </li>
          <li>
            Objects and arrays declared with <code>const</code> can still be{" "}
            <b>modified internally</b>.
          </li>
        </ul>
      </div>

      {/* Example 1: Basic const Declaration */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Basic const Declaration</h2>
        <p>
          Once a variable is declared with <code>const</code>, you can’t change
          its value later.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const academy = "Edu-Tech Master Academy";
console.log(academy);`}
          />
          <TryItButton
            code={`const academy = "Edu-Tech Master Academy";
console.log(academy);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #0ea5e9",
            borderRadius: "8px",
          }}
        >
          Output: <b>Edu-Tech Master Academy</b>
        </div>
      </div>

      {/* Example 2: No Reassignment */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ No Reassignment</h2>
        <p>
          You can’t reassign a <code>const</code> variable. Doing so will cause
          an error.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const academy = "Edu-Tech Master Academy";
academy = "ETMA"; // ❌ Error`}
          />
          <TryItButton
            code={`const academy = "Edu-Tech Master Academy";
academy = "ETMA";`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #ef4444",
            borderRadius: "8px",
          }}
        >
          Output: ❌ TypeError — Assignment to constant variable.
        </div>
      </div>

      {/* Example 3: Must Be Initialized */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Must Be Initialized</h2>
        <p>
          You must assign a value when declaring a <code>const</code> variable.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const academy;
academy = "Edu-Tech Master Academy"; // ❌ Error`}
          />
          <TryItButton
            code={`const academy;
academy = "Edu-Tech Master Academy";`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #f59e0b",
            borderRadius: "8px",
          }}
        >
          Output: ❌ SyntaxError — Missing initializer in const declaration.
        </div>
      </div>

      {/* Example 4: Block Scope */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Block Scope</h2>
        <p>
          <code>const</code> variables are block scoped, similar to{" "}
          <code>let</code>.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const name = "Edu-Tech Master Academy";

{
  const name = "ETMA Inside Block";
  console.log(name);
}

console.log(name);`}
          />
          <TryItButton
            code={`const name = "Edu-Tech Master Academy";

{
  const name = "ETMA Inside Block";
  console.log(name);
}

console.log(name);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #3b82f6",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            ETMA Inside Block
            <br />
            Edu-Tech Master Academy
          </pre>
        </div>
      </div>

      {/* Example 5: const with Objects */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ const with Objects</h2>
        <p>
          You can change the <b>properties</b> of an object declared with{" "}
          <code>const</code>, but not the object itself.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const academy = { name: "Edu-Tech Master Academy", course: "JS" };
academy.course = "React"; // ✅ Allowed
console.log(academy);`}
          />
          <TryItButton
            code={`const academy = { name: "Edu-Tech Master Academy", course: "JS" };
academy.course = "React";
console.log(academy);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #22c55e",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>{`{ name: "Edu-Tech Master Academy", course: "React" }`}</pre>
        </div>
      </div>

      {/* Example 6: const with Arrays */}
      <div className="tutorial-section">
        <h2 className="section-title">6️⃣ const with Arrays</h2>
        <p>
          Arrays declared with <code>const</code> can also be modified (you can
          change, add, or remove elements), but you can’t reassign the array.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const courses = ["HTML", "CSS", "JavaScript"];
courses.push("React"); // ✅ Allowed
console.log(courses);`}
          />
          <TryItButton
            code={`const courses = ["HTML", "CSS", "JavaScript"];
courses.push("React");
console.log(courses);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #16a34a",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>["HTML", "CSS", "JavaScript", "React"]</pre>
        </div>
      </div>

      {/* Best Practices */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🧠 Best Practices</h2>
        <ul className="tutorial-list-points">
          <li>
            ✅ Use <code>const</code> by default unless you need to reassign.
          </li>
          <li>
            ✅ Prefer <code>const</code> for objects and arrays to avoid
            accidental overwrites.
          </li>
          <li>
            ⚠ Don’t use <code>var</code> — stick with <code>let</code> and{" "}
            <code>const</code>.
          </li>
        </ul>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <b>Data Types</b> in JavaScript.
          </li>
          <li>
            Understand <b>Operators</b> and expressions.
          </li>
          <li>
            Explore <b>JavaScript Scope</b> and <b>Hoisting</b>.
          </li>
        </ul>
      </div>
    </div>
  );
}
